/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import { FormControl, Grid } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import useDispatchRegistratinOrganState from '../../../stores/DispatchRegistrationOrganState';
import useDispatchRegistrationState from '../../../stores/DispatchRegistrationState';
import { maskCPF, maskNames, validate } from '../../../utils/stringsManipulations';
import useDispatchState from '~/features/Dispatch/stores/DispatchState';
import Buttons from '../../DispatchRegistrationTab/components/Buttons';
import notify from '~/utils/notify';
import FieldAutocompleteRegistration from '~/components/FieldAutocompleteRegistration';
import { Downgraded, useHookstate } from '@hookstate/core';
import FieldTextRegistration from '~/components/FieldTextRegistration';

interface IFormorgans {
  readonly handleClickOpenConfirmAction: (action: string) => void
}

interface IErrosProps {
  readonly cpf: boolean;
  readonly name: boolean;
  readonly auxiliaryOrgans: boolean;
  readonly actionTaken: boolean;
  readonly registration: boolean;
}

export default function OrgansForm({ handleClickOpenConfirmAction }: IFormorgans) {
  const loading = useHookstate<boolean>(false)
  const { dispatch } = useDispatchState();
  const { occurrence, status } = dispatch();
  const { id: occurrenceIdState, } = occurrence;
  const disableFields = status.value.id === 8
  const { registrationOrganDispatch, sendOrgans, editOrgans, getAuxiliaryOrgans, getActionTaken, deleteOrgans } = useDispatchRegistratinOrganState();

  const { registrationDispatch } = useDispatchRegistrationState();
  const { addRegistration, inputErrors } = registrationDispatch();

  const { organ } = registrationOrganDispatch();


  const { translate } = useLang();

  const [error, setError] = useState<IErrosProps>({
    cpf: false,
    auxiliaryOrgans: false,
    actionTaken: false,
    name: false,
    registration: false,
  });


  const someInvalidInput = () => {
    if (!organ.get().name) {
      inputErrors.name.set(true)
    }
    if (!organ.get().auxiliaryOrgans) {
      inputErrors.auxiliaryOrgans.set(true)
    }

    if (!organ.get().auxiliaryOrgans) {
      inputErrors.actionTaken.set(true)
    }

    return Object.values(inputErrors.attach(Downgraded).get()).some(value => !!value)
  }

  const handleSend = useCallback(async () => {
    if (someInvalidInput()) {
      return
    }
    loading.set(true)
    await sendOrgans(organ.get()).finally(() => {
      loading.set(false)
    })
    notify({ message: translate('Successfully created'), type: 'success' })
    addRegistration.set(false)
  }, [addRegistration, organ, sendOrgans, translate])

  const handleEdit = useCallback(async () => {
    loading.set(true)
    await editOrgans(organ.get()).finally(() => {
      loading.set(false)
    })
    notify({ message: translate('Saved successfully'), type: 'success' })
    addRegistration.set(false)
  }, [addRegistration, editOrgans, organ, translate])

  useEffect(() => {
    organ.set(prev => ({ ...prev, occurrenceId: occurrenceIdState.get() }))
  }, [])


  return (
    <FormControl fullWidth size='small'>
      <Grid container direction='row'>
        <Grid item xs={6} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('Auxiliary Organ')}
            onValueChange={(auxiliaryOrgans) => organ.auxiliaryOrgans.set(auxiliaryOrgans)}
            valueId={organ.auxiliaryOrgans.get()?.id ?? ''}
            optionsFetch={getAuxiliaryOrgans}
            isRequired
            disabled={disableFields}
            error={inputErrors.auxiliaryOrgans.get()}
            onError={(value) => inputErrors.auxiliaryOrgans.set(value)}
          />
        </Grid>
        <Grid item xs={6} sx={{ padding: 1 }}>
          <FieldAutocompleteRegistration
            label={translate('Action Taken')}
            onValueChange={(actionTaken) => organ.actionTaken.set(actionTaken)}
            valueId={organ.actionTaken.get()?.id ?? ''}
            optionsFetch={getActionTaken}
            isRequired
            disabled={disableFields}
            error={inputErrors.actionTaken.get()}
            onError={(value) => inputErrors.actionTaken.set(value)}
          />
        </Grid>
        <Grid item xs={6} sx={{ padding: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            label={'Receiver name'}
            value={organ.name.get()}
            onChangeValue={(value) => organ.name.set(value)}
            inputErrors={inputErrors.name}
            required={true}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <TextField
            disabled={disableFields}
            fullWidth
            error={!!error.cpf}
            size='small'
            id="outlined-cpf"
            label={translate('Receiver CPF')}
            value={organ.cpf.get()}
            onChange={(event) => {
              const cpf = maskCPF(event.target.value)
              organ.cpf.set(cpf)
              if (validate(cpf) || cpf.length === 14) {
                setError({ ...error, cpf: false, registration: false })
              } else {
                setError({
                  ...error,
                  cpf: true
                })
              }
            }}
          />
        </Grid>
        <Grid item xs={3} sx={{ padding: 1 }}>
          <TextField
            disabled={disableFields}
            fullWidth
            size='small'
            id="outlined-registration"
            error={!!error.registration}
            label={translate('Receiver Registry')}
            value={organ.registration.get()}
            InputProps={{
              onChange: (event) => {
                const registration = event.target.value
                organ.registration.set(registration)
                if (registration.length) {
                  setError({ ...error, cpf: false, registration: false })
                }
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{ padding: 1, marginBottom: 1 }}>
          <FieldTextRegistration
            disableFields={disableFields}
            label={'Note'}
            value={organ.notes.get()}
            onChangeValue={(value) => organ.notes.set(value)}
            multiline={true}
            rows={4}
            height={92}
          />
        </Grid>
        <Grid item xs={12}>
          <Buttons
            loading={loading}
            isUpdate={!!organ.id.get()}
            handleClickOpenConfirmAction={handleClickOpenConfirmAction}
            handleEdit={handleEdit}
            handleSend={handleSend}
            someInvalidInput={someInvalidInput}
            disableFields={disableFields}
          />
        </Grid>
      </Grid>
    </FormControl >

  );
}